"use client";

import { useEffect, useState } from "react";

import { DxIcon } from "../../../dx/ui/icons";
import type { BrowseArea } from "../../_hooks/use-room-type-search";
import { FilterChip } from "../../ui/filter-chip";
import { TriggerField } from "../../ui/trigger-field";

import { useTriggerToggle } from "./_hooks/use-trigger-toggle";
import { AreaFilter } from "./area-filter";
import { FilterPopupShell } from "./filter-popup-shell";

/**
 * エリアフィルター: サイドバーセクション（ラベル＋トリガー＋適用チップ＋ポップアップ）。
 *
 * 状態モデル:
 * - ポップアップ内操作は draft Set をトグル（即時適用しない）。
 * - 開く度に draft を確定値（selectedAreaSlugs）から再初期化。
 * - 決定: draft を一括適用（onApplyAreaSlugs）→ close。
 * - クリア（ポップアップ内）: draft を空に → 未適用（決定で反映）。
 * - チップ×: 即時適用（onToggleArea）。
 * - セクションクリア: 一括即時適用（onApplyAreaSlugs([])）。
 */
export function AreaFilterPopup({
  areas,
  selectedAreaSlugs,
  onToggleArea,
  onApplyAreaSlugs,
  externalOpen,
  onExternalClose,
  variant = "filter-sidebar",
  externalAnchorEl,
}: {
  areas: BrowseArea[];
  selectedAreaSlugs: ReadonlySet<string>;
  /** チップ× など即時1件トグル */
  onToggleArea: (slug: string) => void;
  /** 決定・セクションクリア時の一括適用 */
  onApplyAreaSlugs: (slugs: string[]) => void;
  /** SP 検索バーなど外部からポップアップを開く場合に true を渡す */
  externalOpen?: boolean;
  /** externalOpen で開いたポップアップが閉じられたときのコールバック */
  onExternalClose?: () => void;
  variant?: "filter-sidebar" | "filter-search";
  /**
   * 外部からアンカー要素を渡す場合に使用（SP 検索バーなど）。
   * 指定時は内部 anchorEl より優先される。
   */
  externalAnchorEl?: HTMLElement | null;
}) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // ポップアップ内ドラフト。Set は immutable に更新する（useState パターン）。
  const [draftSlugs, setDraftSlugs] = useState<Set<string>>(new Set());

  const selectedAreas = areas.filter((a) => selectedAreaSlugs.has(a.slug));
  const hasSelection = selectedAreaSlugs.size > 0;

  const triggerLabel = hasSelection ? "エリア選択あり" : "エリア";

  const handleOpen = () => {
    // 開く度に確定値でドラフトを再初期化
    setDraftSlugs(new Set(selectedAreaSlugs));
    setOpen(true);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // × / 外側クリック / Esc → ドラフト破棄（何もしない）
      setOpen(false);
      onExternalClose?.();
    }
  };

  // トリガー（外部 anchor）を開閉トグルにする（outside-press との競合を回避）
  const triggerToggle = useTriggerToggle(open, handleOpen, () =>
    setOpen(false),
  );

  // externalOpen が true になったら Drawer を開く（SP 検索バーからのトリガー）
  useEffect(() => {
    if (externalOpen) {
      handleOpen();
    } else if (externalOpen === false) {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  const handleDraftToggle = (slug: string) => {
    setDraftSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleClear = () => {
    // ドラフトを空にするだけ（未適用）
    setDraftSlugs(new Set());
  };

  const handleConfirm = () => {
    onApplyAreaSlugs([...draftSlugs]);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-[8px]">
      <p className="font-notojp text-dx-text-default text-[14px] leading-[24px] font-bold">
        エリア
      </p>

      {/* callback ref: useRef は react-hooks/refs lint 警告を引くため */}
      <div
        ref={(el) => setAnchorEl(el)}
        onPointerDown={triggerToggle.onPointerDown}
      >
        <TriggerField
          icon={<DxIcon src="/i/dx/map.svg" className="text-dx-text-default" />}
          label={triggerLabel}
          hasValue={hasSelection}
          onClick={triggerToggle.onClick}
          data-analytics-id="room_type_browse_opened_area_filter"
        />
      </div>

      {hasSelection && (
        <div className="flex items-center gap-[8px] px-[16px] py-[8px]">
          <div className="flex flex-wrap gap-[8px]">
            {selectedAreas.map((area) => (
              <FilterChip
                key={area.id}
                label={area.name}
                onRemove={() => onToggleArea(area.slug)}
              />
            ))}
          </div>
          <button
            data-analytics-id="room_type_browse_clicked_area_clear"
            type="button"
            onClick={() => onApplyAreaSlugs([])}
            className="font-notojp text-dx-text-default focus-visible:ring-dx-border-focus shrink-0 rounded-sm text-[12px] leading-none underline focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          >
            クリア
          </button>
        </div>
      )}

      {/* エリアポップアップ: 幅はビューポート連動(320/400)。列数は AreaFilter 側 flex-wrap で自動(320→2列/400→3列) */}
      <FilterPopupShell
        open={open}
        onOpenChange={handleOpenChange}
        anchorEl={externalAnchorEl ?? anchorEl}
        title="エリアを選択"
        canClear={draftSlugs.size > 0}
        canConfirm={true}
        onClear={handleClear}
        onConfirm={handleConfirm}
        variant={variant}
      >
        <AreaFilter
          areas={areas}
          selectedAreaSlugs={draftSlugs}
          onToggleArea={handleDraftToggle}
        />
      </FilterPopupShell>
    </div>
  );
}
